import React, { useState, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { AuthContext } from '../../../../../context/AuthContext';
import { EditorContent } from '@tiptap/react';
import EditorToolbar from '../../../../../components/editor/EditorToolbar';
import tiptapExtensions from '../../../../../components/editor/tiptapExtensions';
import useEditorWithDragDrop from '../../../../../components/editor/useEditorWithDragDrop';
import FileAttachment from '../../../../../components/FileAttachment/FileAttachment';

function SafetySummaryModal({ show, mode, initialData, onClose, onSave, onDelete, onStartEdit, isLocked, canEdit }) {
  const { user } = useContext(AuthContext);

  const userName = user?.UserName || user?.name || '담당자';
  const userPosition = user?.position || '';
  const userDisplayName = userPosition ? `${userName} ${userPosition}` : userName;

  const getTodayStr = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const [writer, setWriter] = useState('');
  const [writeDate, setWriteDate] = useState('');
  const [files, setFiles] = useState([]);
  const [editorHeight, setEditorHeight] = useState('380px');

  const isView = mode === 'DETAIL';

  // Tiptap 에디터 Hook
  const editor = useEditorWithDragDrop(tiptapExtensions);

  useEffect(() => {
    if (show) {
      setWriter(initialData?.writer || userDisplayName);
      setWriteDate(initialData?.writeDate || getTodayStr());
      setFiles(initialData?.files || []);
      setEditorHeight('380px');

      if (editor && mode === 'WRITE') {
        editor.commands.setContent(initialData?.conclusion || '');
      }
    }
  }, [show, initialData, mode, userDisplayName, editor]);

  if (!show) return null;

  const handleSave = (e) => {
    if (e) e.preventDefault();
    const contentHTML = editor ? editor.getHTML() : '';
    const contentText = editor ? editor.getText().trim() : '';

    if (!contentText) {
      return alert('종합 총평 내용을 입력해 주세요.');
    }

    onSave({
      writer,
      writeDate,
      conclusion: contentHTML,
      files
    });
  };

  return ReactDOM.createPortal(
    <div className="modal show d-block" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9998, overflow: 'hidden' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '1000px', width: '95%' }}>
        <div className="modal-content" style={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', overflow: 'hidden' }}>

          {/* 회의록 모달과 100% 동일한 파란 그라데이션 모달 헤더 바 */}
          <div className="modal-header d-flex justify-content-between align-items-center" style={{ background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', color: '#fff', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', padding: '16px 24px' }}>
            <h5 className="modal-title fw-bold m-0 fs-5">
              <i className={`fas ${isView ? 'fa-eye' : 'fa-edit'} me-2`}></i>
              {isView ? '위험성평가 종합 총평 상세' : (initialData?.conclusion ? '위험성평가 종합 총평 수정' : '새 위험성평가 종합 총평 작성')}
            </h5>
            <div className="d-flex align-items-center gap-3">
              <button type="button" className="btn-close btn-close-white" onClick={onClose} style={{ boxShadow: 'none' }}></button>
            </div>
          </div>

          {/* 회의록 모달 100% 동일 본문 */}
          <div className="modal-body" style={{ padding: '20px 30px', height: '65vh', minHeight: '450px', overflowY: 'auto', backgroundColor: '#f8fafc' }}>
            <div className="row g-3">
              {/* 작성자 (readOnly) */}
              <div className="col-md-6">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-user me-1 text-primary"></i> 작성자
                  </label>
                  <div className="text-dark px-3 py-2 rounded" style={{ fontSize: '0.825rem', backgroundColor: '#f1f5f9', fontWeight: 600 }}>
                    {writer || '-'}
                  </div>
                </div>
              </div>

              {/* 작성일 (readOnly) */}
              <div className="col-md-6">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-calendar-alt me-1 text-primary"></i> 작성일
                  </label>
                  <div className="text-dark px-3 py-2 rounded" style={{ fontSize: '0.825rem', backgroundColor: '#f1f5f9', fontWeight: 600 }}>
                    {writeDate || '-'}
                  </div>
                </div>
              </div>

              {/* 종합 총평 내용 (Tiptap 에디터) */}
              <div className="col-12">
                <div className="p-0 bg-white d-flex flex-column mt-2" style={{ height: editorHeight, transition: 'height 0.3s ease' }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                      <i className="fas fa-file-alt me-1 text-primary"></i> 종합 총평 내용 {!isView && <span className="text-danger">*</span>}
                    </label>
                    {!isView && (
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-secondary fw-semibold" style={{ fontSize: '0.8rem' }}>에디터 높이:</span>
                        <select
                          className="form-select form-select-sm text-secondary"
                          style={{ width: '130px', fontSize: '0.8rem', cursor: 'pointer' }}
                          value={editorHeight}
                          onChange={(e) => setEditorHeight(e.target.value)}
                        >
                          <option value="380px">기본 (380px)</option>
                          <option value="550px">넓게 (550px)</option>
                          <option value="750px">아주 넓게 (750px)</option>
                        </select>
                      </div>
                    )}
                  </div>
                  <div className="flex-grow-1 bg-light rounded-2 p-1" style={{ minHeight: 0 }}>
                    {isView ? (
                      <div className="post-content bg-white p-3 rounded shadow-sm h-100 overflow-auto">
                        <style>{`
                          .post-content p { margin-top: 0; margin-bottom: 0.2em; font-size: 10pt; }
                          .post-content { font-size: 10pt; line-height: 1.2; }
                        `}</style>
                        <div dangerouslySetInnerHTML={{ __html: initialData?.conclusion || '' }} />
                      </div>
                    ) : (
                      <div className="border p-1 rounded bg-white h-100 d-flex flex-column">
                        {editor && <EditorToolbar editor={editor} />}
                        <div className="flex-grow-1 overflow-auto bg-white" style={{ minHeight: 0 }}>
                          <style>{`
                            .ProseMirror p { margin-top: 0; margin-bottom: 0.2em; font-size: 10pt; }
                            .ProseMirror { font-size: 10pt; line-height: 1.2; }
                          `}</style>
                          <EditorContent editor={editor} className="editor-content h-100" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 비고 (첨부파일) */}
              {(!isView || (files && files.filter(f => !f.isDeleted).length > 0)) && (
                <div className="col-12">
                  <div className="p-0 bg-white d-flex flex-column mt-4">
                    <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                      <i className="fas fa-paperclip me-1 text-primary"></i> 비고 (첨부파일)
                    </label>
                    <div className="flex-grow-1 overflow-auto bg-light rounded-2 p-1" style={{ minHeight: 0 }}>
                      <FileAttachment files={files} setFiles={setFiles} mode={isView ? 'view' : 'edit'} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 회의록 모달 100% 동일 푸터 */}
          <div className="modal-footer" style={{ borderTop: '1px solid #cbd5e1', backgroundColor: '#f1f5f9', padding: '12px 24px', display: 'flex', justifyContent: 'flex-end' }}>
            <div className="d-flex" style={{ gap: '8px' }}>
              {isView ? (
                <>
                  {canEdit && <button type="button" className="btn btn-primary btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={onStartEdit}>수정</button>}
                  {canEdit && <button type="button" className="btn btn-danger btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={onDelete}>삭제</button>}
                  <button type="button" className="btn btn-secondary btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={onClose}>닫기</button>
                </>
              ) : (
                <>
                  <button type="button" className="btn btn-primary btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={handleSave}>저장</button>
                  <button type="button" className="btn btn-secondary btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={onClose}>취소</button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default SafetySummaryModal;
